import { useStorage } from '@vueuse/core'
// Return text contrasting color based on background color
const getContrastTextColor = (color) => {
  if(!color) return "#000000"

  let r, g, b

  // Create a temporary canvas element to get computed color from named colors
  const ctx = document.createElement("canvas").getContext("2d")
  ctx.fillStyle = color
  const computedColor = ctx.fillStyle // Returns color in RGB or HEX format

  // If the color is in RGB format
  if (computedColor.startsWith("rgb")) {
    const rgbValues = computedColor.match(/\d+/g)
    if (rgbValues?.length === 3) {
      [r, g, b] = rgbValues.map(Number)
    }
  }
  // If the color is in HEX format
  else if (computedColor.startsWith("#")) {
    if (computedColor.length === 4) { // Short HEX format (#f53)
      r = parseInt(computedColor[1] + computedColor[1], 16)
      g = parseInt(computedColor[2] + computedColor[2], 16)
      b = parseInt(computedColor[3] + computedColor[3], 16)
    } else { // Full HEX format (#ff5733)
      r = parseInt(computedColor.substr(1, 2), 16)
      g = parseInt(computedColor.substr(3, 2), 16)
      b = parseInt(computedColor.substr(5, 2), 16)
    }
  }

  // If the color could not be parsed, return default black text
  if (r === undefined || g === undefined || b === undefined) {
    return "#000000"
  }

  // Calculate WCAG relative luminance with sRGB linearization
  const toLinear = (c) => {
    const s = c / 255
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }

  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)

  // Contrast ratio against white (L=1) and black (L=0)
  const contrastWithWhite = (1 + 0.05) / (L + 0.05)
  const contrastWithBlack = (L + 0.05) / 0.05

  return contrastWithBlack >= contrastWithWhite ? "#000000" : "#FFFFFF"
}

const getSafeColor = (color) => {
  return !color || color === "transparent" ? "#000000" : color
}

const hslToHex = (h, s, l) => {
  s /= 100
  l /= 100

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c / 2

  let r = 0
  let g = 0
  let b = 0

  if (h < 60) {
    r = c
    g = x
  } else if (h < 120) {
    r = x
    g = c
  } else if (h < 180) {
    g = c
    b = x
  } else if (h < 240) {
    g = x
    b = c
  } else if (h < 300) {
    r = x
    b = c
  } else {
    r = c
    b = x
  }

  const toHex = value =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Stable color for a person - the same name and id always give the same color.
 * @param {string} name
 * @param {number|string} id
 * @returns {string} hex color
 */
const userColor = (name, id) => {
  const input = `${name}-${id}`

  let hash = 0

  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash)
  }

  const hue = Math.abs(hash) % 360

  return hslToHex(hue, 65, 55)
}

/**
 * Applies a color theme to the layout wrapper by managing CSS classes.
 *
 * This function handles both setting and retrieving color themes:
 * - When a theme parameter is provided, it saves the theme to localStorage and applies it
 * - When called without parameters, it applies the previously stored theme from localStorage
 * - Default color theme is '' (no theme)
 * Available themes: 'default-theme', 'orange-theme', 'blue-theme', 'high-contrast-theme'
 *
 * @param {string} [theme] - Optional theme name to apply and store. If omitted, applies stored theme.
 * @example
 * // Store and apply a new theme
 * applyStoredColorTheme('orange-theme')
 *
 * // Apply the stored theme (e.g., on component mount)
 * applyStoredColorTheme()
 */
const applyStoredColorTheme = (theme) => {
  const colorThemes = ['default-theme', 'orange-theme', 'blue-theme', 'high-contrast-theme']
  const colorVariant = useStorage('color-variant', 'default-theme')

  // If theme is provided, update storage
  if (theme && colorThemes.includes(theme)) {
    colorVariant.value = theme
  }

  // Apply the stored theme
  if (colorThemes.includes(colorVariant.value)) {
    const layoutWrapper = document.querySelector('.v-application__wrap')
    if (!layoutWrapper) return
    colorThemes.forEach(ct => layoutWrapper.classList.remove(ct))
    layoutWrapper.classList.add(colorVariant.value)
  }
}

export {
  getContrastTextColor,
  getSafeColor,
  userColor,
  applyStoredColorTheme,
}
